import { CSSProperties } from "react";
import { useWatch } from "react-hook-form";

const defaultStyle: CSSProperties = {
  display: "block",
  marginTop: "-20px",
  marginBottom: "12px",
  fontSize: "13px",
  color: "#666666",
};

const toUtcIso = (rawValue: unknown): string | null => {
  if (!rawValue) return null;

  if (typeof rawValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
    return `${rawValue}T00:00:00.000Z`;
  }

  // DateTimeInput values can be timezone-less strings; treat them as UTC wall-clock inputs.
  if (typeof rawValue === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/.test(rawValue)) {
    return `${rawValue}Z`;
  }

  if (rawValue instanceof Date) {
    if (Number.isNaN(rawValue.getTime())) return null;
    return rawValue.toISOString();
  }

  if (typeof rawValue === "string") {
    const parsed = new Date(rawValue);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }
  }

  return null;
};

const formatInBrowserTimezone = (utcIso: string): string | null => {
  const parsed = new Date(utcIso);
  if (Number.isNaN(parsed.getTime())) return null;

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  }).format(parsed);
};

interface LocalisedDateInputPreviewProps {
  source: string;
  label: string;
  text?: string;
  style?: CSSProperties;
}

const LocalisedDateInputPreview = ({
  source,
  label,
  text = "In your timezone:",
  style = defaultStyle,
}: LocalisedDateInputPreviewProps) => {
  const value = useWatch({ name: source });
  const normalizedValue = toUtcIso(value);
  const localisedValue = normalizedValue ? formatInBrowserTimezone(normalizedValue) : null;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (!localisedValue) return null;

  return (
    <span style={style} title={`${label} in ${timeZone}`}>
      {`${text} ${localisedValue}`}
    </span>
  );
};

export default LocalisedDateInputPreview;
